import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { tooltipStyle, COLORS } from '../../utils/colors';
import { useColorScheme } from '../../hooks/useColorScheme';

/**
 * Line chart for allocation or cohort trends over time.
 *
 * Props:
 *   data.dates  — ["2025-01-01", "2025-02-01", ...]
 *   data.series — [{ name: "Option A", data: [60.1, 61.3, ...] }, ...]
 *   colors      — optional color override array
 *   yMax        — optional max for y axis (default 100)
 *   yLabel      — optional y-axis label
 */
export default function TrendChart({ data, colors, yMax, yLabel }) {
  const { palette: schemePalette } = useColorScheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data?.dates) return;

    if (chartRef.current) chartRef.current.destroy();

    const palette = colors || schemePalette;

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    const labels = data.dates.map(formatDate);

    const datasets = data.series.map((s, i) => ({
      label: s.name,
      data: s.data,
      borderColor: palette[i % palette.length],
      backgroundColor: palette[i % palette.length],
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.3,
      fill: false
    }));

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 10 },
              color: COLORS.textMuted,
              maxRotation: 45,
              autoSkip: true,
              maxTicksLimit: 12
            }
          },
          y: {
            beginAtZero: yMax != null,
            ...(yMax != null ? { max: yMax } : { grace: '15%' }),
            grid: { color: '#f0f1f3' },
            ticks: { font: { size: 11 }, color: COLORS.textMuted },
            title: yLabel ? {
              display: true,
              text: yLabel,
              font: { size: 11 },
              color: COLORS.textMuted
            } : undefined
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              label: (item) => ` ${item.dataset.label}: ${item.raw?.toFixed(1)}`
            }
          },
          datalabels: { display: false }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data, colors, yMax, yLabel, schemePalette]);

  return <canvas ref={canvasRef} />;
}
